import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, runBootstrap, useApi } from './client'
import type {
  License,
  LicenseDebtSummary,
  LicenseDetail,
  LicensePayment,
  LicenseStatusFilter,
  LinkableTransaction,
  LinkPaymentResult,
  PayVendorResult,
} from './types'

const ROOT = '/licenses'
const KEY = ['licenses'] as const

type ListResp = { items: License[]; nextCursor: string | null }

export type LicensesFilter = {
  organizationId?: string
  counterpartyId?: string
  tariffId?: string
  bitrixStageId?: string
  commitmentStatus?: LicenseStatusFilter
  validToBefore?: string
}

export function useLicenses(filter: LicensesFilter = {}) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: [...KEY, 'list', filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api<ListResp>(
        `${ROOT}${buildQuery({
          organization_id: filter.organizationId,
          counterparty_id: filter.counterpartyId,
          tariff_id: filter.tariffId,
          bitrix_stage_id: filter.bitrixStageId,
          commitment_status: filter.commitmentStatus,
          valid_to_before: filter.validToBefore,
          cursor: pageParam,
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
}

export function flattenLicenses(data: { pages?: ListResp[] } | undefined): License[] {
  if (!data?.pages) return []
  return data.pages.flatMap((p) => p.items)
}

// Aggregate "our debt to the vendor" across pre-purchase licenses. Honors the
// same org/counterparty filters as the list so the banner matches the view.
export function useLicenseDebtSummary(
  filter: Pick<LicensesFilter, 'organizationId' | 'counterpartyId'> = {},
  opts?: { enabled?: boolean },
) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'debt-summary', filter],
    enabled: opts?.enabled ?? true,
    queryFn: () =>
      api<LicenseDebtSummary>(
        `${ROOT}/debt-summary${buildQuery({
          organization_id: filter.organizationId,
          counterparty_id: filter.counterpartyId,
        })}`,
      ),
  })
}

export function useLicense(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: id != null,
    queryFn: () => api<LicenseDetail>(`${ROOT}/${id}`),
  })
}

export type LinkableQuery = {
  direction: 'in' | 'out'
  search?: string
  limit?: number
}

export function useLinkableTransactions(licenseId: string | null, q: LinkableQuery | null) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'linkable', licenseId, q],
    enabled: licenseId != null && q != null,
    queryFn: () =>
      api<{ items: LinkableTransaction[]; hasMore: boolean }>(
        `${ROOT}/${licenseId}/linkable-transactions${buildQuery({
          direction: q?.direction,
          search: q?.search,
          limit: q?.limit,
        })}`,
      ),
  })
}

export type PayVendorInput = {
  licenseId: string
  amount: number
  accountId: string
  date: string
  categoryId: string
  description?: string
}

export function usePayVendor() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PayVendorInput) =>
      api<PayVendorResult>(`${ROOT}/${input.licenseId}/pay-vendor`, {
        method: 'POST',
        body: {
          amount: input.amount,
          accountId: input.accountId,
          date: input.date,
          categoryId: input.categoryId,
          description: input.description ?? null,
        },
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.licenseId] })
      qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

export type LinkPaymentInput = {
  licenseId: string
  transactionId: string
  direction: 'in' | 'out'
  amount: number
}

export function useLinkPayment() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: LinkPaymentInput) =>
      api<LinkPaymentResult>(`${ROOT}/${input.licenseId}/payments`, {
        method: 'POST',
        body: {
          transactionId: input.transactionId,
          direction: input.direction,
          amount: input.amount,
        },
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.licenseId] })
    },
  })
}

export type UnlinkPaymentInput = {
  licenseId: string
  paymentId: string
}

export function useUnlinkPayment() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UnlinkPaymentInput) =>
      api<void>(`${ROOT}/${input.licenseId}/payments/${input.paymentId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.licenseId] })
    },
  })
}

export type UpdateLicenseInput = {
  licenseId: string
  /** Tariff to pin, or null to clear the binding and hand it back to sync. */
  tariffId?: string | null
  /** Vendor promo on this license (0–100). Cuts what we owe the vendor. */
  discountPercent?: number
}

// The license card's hand edits: pin (or clear) the tariff, set the vendor
// discount. Either field may travel alone. Invalidates the list (and debt
// summary, which shares the root key) plus this license's detail.
export function useUpdateLicense() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateLicenseInput) =>
      api<LicenseDetail['license']>(`${ROOT}/${input.licenseId}`, {
        method: 'PATCH',
        body: {
          ...(input.tariffId !== undefined ? { tariffId: input.tariffId } : {}),
          ...(input.discountPercent !== undefined
            ? { discountPercent: input.discountPercent }
            : {}),
        },
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.licenseId] })
    },
  })
}

export function useBootstrapLicenses() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => runBootstrap(api, '/b24/bootstrap-licenses'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

export type { LicensePayment }
